import { isNull, isNumber } from 'lodash';
import React, { useRef } from 'react';
import { ILayerProps, IMarkupLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { FontSizeSelector } from '@meitu/saas-editor-ui/es/components/font-size-selector';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useGlobalWidget } from '@meitu/saas-editor-ui/es/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';

const fontSizeList = [
    12, 14, 18, 24, 30, 36, 48, 60, 72, 84, 96, 108, 120, 140, 150, 160, 180, 200, 250, 300, 400, 500,
];

export const LabelFontSize: React.FC<{
    id: string;
    onAfterChange?: Function;
    trackCode?: {
        color?: string | number;
        size?: string | number;
    };
}> = React.memo(({ id, trackCode, onAfterChange }) => {
    const { $t } = useLocaleStore();
    const oldLayer = useRef<ILayerProps | null>(null);
    const res = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;
        const { label } = layer.data as IMarkupLayerProps;
        return {
            color: label.color,
            size: label.fontSize || 12,
        };
    });

    const colorGlobalWidgetRef = useGlobalWidget(editor, false);
    const fontSizeGlobalWidgetRef = useGlobalWidget(editor, false);

    if (isNull(res)) return null;

    const { color, size } = res;

    const handleChange = (val: number | null, e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (isNumber(val)) {
            if (fontSizeList[0] <= val && val <= fontSizeList[fontSizeList.length - 1]) {
                const layer = editor.services.flatLayer.getLayer(id, true);
                editor.markupLayer.setAttrOperateOriginData(layer);
                editor.markupLayer.setLabelFontSize(id, val);
                editor.markupLayer.clearAttrOperateOriginData();
                onAfterChange?.(trackCode?.size);
            }
        }
    };

    return (
        <div className="c-markup-panel__item">
            <LabelAction title={$t((v) => v.panel.markupPanel.fontSize)} align={true}>
                <div className="c-markup-label__fontsize">
                    <FontSizeSelector
                        value={size}
                        fontSizeOptions={fontSizeList}
                        onChange={handleChange}
                        onOpenChange={(e) => {
                            fontSizeGlobalWidgetRef.current = e;
                        }}
                    />
                    <ColorGroup
                        popupTriggerSize={{
                            width: 78,
                            height: 34,
                        }}
                        color={color}
                        setColor={(val: string) => {
                            if (oldLayer.current === null) {
                                oldLayer.current = editor.services.flatLayer.getLayer(id, true);
                            }
                            editor.markupLayer.setLabelColor(id, val);
                        }}
                        setColorAfter={(val: string) => {
                            if (oldLayer.current) {
                                editor.markupLayer.setLabelColor(oldLayer.current.id, val, oldLayer.current);
                            }
                            oldLayer.current = null;
                            onAfterChange?.(trackCode?.color);
                        }}
                        isGradient={false}
                        popupOverlayClassName="c-markup-panel__color-picker"
                        onToggleBizTab={(bizTab) => {
                            if (bizTab === 'team') {
                                window._stat_track?.('hb_layers_func_click', {
                                    func_id: '8_107',
                                });
                            }
                        }}
                        onOpenChange={(e) => {
                            colorGlobalWidgetRef.current = e;
                        }}
                    />
                </div>
            </LabelAction>
        </div>
    );
});
